/**
 * The traces list: one row per chat, newest activity first (SUP-273 P3).
 *
 * Rolled up to chats rather than to submissions, because a submission is not
 * a unit anyone thinks in: den mints one per parked tool call, so a single
 * request from the user settles as several. A chat's turns are one level
 * down, its steps two.
 *
 * Sub-agent tokens (researcher, delegate) are NOT in these totals.
 * `runSubAgent` returns only the sub-agent's text, so its usage never reaches
 * the settle envelope, and a chat that ran a planset through `delegate` cost
 * materially more than the row says. The detail view flags the turns this
 * affects; the fix is to return and persist nested usage, which is a change
 * to the turn seam, not to this view.
 */
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { client } from "@/client";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  cacheHitRate,
  formatCost,
  formatDuration,
  formatPercent,
  sumUsage,
  usageCost,
} from "@/lib/trace";

export const Route = createFileRoute("/traces/")({
  component: TracesPage,
});

async function fetchChats() {
  const res = await client.api.traces.$get({
    query: { limit: "100" },
  });
  if (!res.ok) throw new Error("Failed to load traces");
  return res.json();
}

type ChatList = Awaited<ReturnType<typeof fetchChats>>;
type ChatSummary = ChatList[number];

/** The provider prefix is the same on every row; the model is the fact. */
function shortModel(modelId: string) {
  return modelId.includes(":")
    ? modelId.split(":").slice(1).join(":")
    : modelId;
}

function TracesPage() {
  const {
    data: chats = [] as ChatSummary[],
    isPending,
    isError,
  } = useQuery({
    queryKey: ["traces"],
    queryFn: fetchChats,
  });

  return (
    <div className="mx-auto min-h-screen max-w-6xl bg-background px-6 py-10">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Agent Traces
          </h1>
          <p className="text-sm text-muted-foreground">
            One row per chat, totalled across its turns
          </p>
        </div>
        <Link to="/" className="text-sm text-muted-foreground hover:underline">
          Back to home
        </Link>
      </div>

      {isPending ? (
        <p className="text-muted-foreground">Loading traces...</p>
      ) : isError ? (
        <p className="text-destructive">Failed to load traces.</p>
      ) : chats.length === 0 ? (
        <p className="text-muted-foreground">
          No traces yet. Run an agent to see traces here.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Chat</TableHead>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Turns</TableHead>
              <TableHead className="text-right">Tokens</TableHead>
              <TableHead className="text-right">Cache hit</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Agent time</TableHead>
              <TableHead>Last active</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {chats.map((chat) => {
              const tokens = sumUsage(chat.usage);
              return (
                <TableRow key={chat.chat_id}>
                  <TableCell>
                    <Link
                      to="/traces/chats/$chatId"
                      params={{ chatId: chat.chat_id }}
                      className="font-medium hover:underline"
                    >
                      {chat.title ?? "Untitled chat"}
                    </Link>
                    {chat.failed_count > 0 && (
                      <Badge variant="destructive" className="ml-2">
                        {chat.failed_count} failed
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {chat.usage.map((u) => shortModel(u.model_id)).join(", ")}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {chat.turn_count}
                    {chat.run_count > chat.turn_count && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({chat.run_count} runs)
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {(
                      tokens.input_tokens + tokens.output_tokens
                    ).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatPercent(cacheHitRate(tokens))}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatCost(usageCost(chat.usage))}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDuration(chat.duration_ms)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(chat.last_settled_ms).toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
